<template>
  <el-dialog
    title="选择商品"
    :visible.sync="dialogVisible"
    width="760px"
    append-to-body
    class="mall-product-picker-dialog"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div class="picker-toolbar">
      <el-input
        v-model="keyword"
        clearable
        placeholder="搜索商品"
        prefix-icon="el-icon-search"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-button type="primary" @click="handleSearch">搜索</el-button>
    </div>

    <div v-loading="loading" class="picker-body">
      <div v-if="list.length" class="product-grid">
        <div
          v-for="item in list"
          :key="item.id"
          class="product-card"
          :class="{ active: String(selectedId) === String(item.id) }"
          @click="handleSelect(item)"
        >
          <div class="product-card-cover-wrap">
            <img
              v-if="productCoverUrl(item)"
              class="product-card-cover"
              :src="resolveMediaUrl(productCoverUrl(item))"
              alt=""
            >
            <div v-else class="product-card-cover product-card-cover-empty">无封面</div>
            <span class="product-card-play" @click.stop="openPreview(item)">
              <i class="el-icon-caret-right" />
            </span>
          </div>
          <div class="product-card-title">{{ item.title || '未命名商品' }}</div>
          <div class="product-card-meta">
            <span class="product-card-price">{{ formatPrice(item.price) }}</span>
            <span class="product-card-category">{{ item.categoryName || '-' }}</span>
          </div>
        </div>
      </div>
      <el-empty v-else description="暂无商品" :image-size="88" />
    </div>

    <div class="picker-footer">
      <el-pagination
        v-if="total > pageSize"
        background
        layout="prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="fetchList"
      />
      <div v-else-if="list.length" class="picker-footer-tip">已展示全部</div>
    </div>

    <el-dialog
      title="商品预览"
      :visible.sync="previewVisible"
      width="640px"
      append-to-body
      @closed="clearPreview"
    >
      <video
        v-if="previewMedia && previewMedia.type === 'video'"
        class="preview-media"
        :src="resolveMediaUrl(previewMedia.url)"
        :poster="resolveMediaUrl(previewPoster)"
        controls
        autoplay
      />
      <img
        v-else-if="previewMedia"
        class="preview-media preview-image"
        :src="resolveMediaUrl(previewMedia.url)"
        alt=""
      >
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getMallProductsPage } from '../../api/mall'
import { resolveMediaUrl } from '../../utils/format'

const PAGE_SIZE = 10

export default {
  name: 'MallProductPickerDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedId: {
      type: [String, Number],
      default: ''
    },
    categoryId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      keyword: '',
      list: [],
      loading: false,
      page: 1,
      pageSize: PAGE_SIZE,
      total: 0,
      previewVisible: false,
      previewMedia: null,
      previewPoster: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    }
  },
  methods: {
    resolveMediaUrl,
    handleOpen() {
      this.page = 1
      this.fetchList()
    },
    handleClosed() {
      this.keyword = ''
      this.list = []
      this.page = 1
      this.total = 0
      this.clearPreview()
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    async fetchList() {
      if (!this.categoryId) {
        this.list = []
        this.total = 0
        return
      }
      this.loading = true
      try {
        const data = await getMallProductsPage({
          keyword: (this.keyword || '').trim(),
          categoryId: this.categoryId,
          status: 2,
          page: this.page,
          pageSize: this.pageSize
        })
        this.list = (data && data.records) || []
        this.total = Number((data && data.total) || 0)
        this.page = Number((data && data.page) || this.page)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    parseProductMedia(rawValue, coverUrl) {
      const items = []
      const pushItem = (value, type) => {
        const url = typeof value === 'string' ? value.trim() : ''
        if (!url || items.some((item) => item.url === url)) return
        items.push({ type: type || this.guessMediaType(url), url })
      }
      if (rawValue) {
        try {
          const parsed = JSON.parse(rawValue)
          if (Array.isArray(parsed)) {
            parsed.forEach((item) => {
              if (typeof item === 'string') {
                pushItem(item)
              } else if (item && item.url) {
                pushItem(item.url, item.type)
              }
            })
          }
        } catch (error) {
          String(rawValue).split(',').forEach((url) => pushItem(url))
        }
      }
      pushItem(coverUrl, 'image')
      return items
    },
    guessMediaType(url) {
      return /\.(mp4|mov|m4v|3gp|avi)$/i.test(url || '') ? 'video' : 'image'
    },
    productCoverUrl(item) {
      if (!item) return ''
      if (item.coverUrl) return item.coverUrl
      const firstImage = this.parseProductMedia(item.imageUrls, '').find((media) => media.type === 'image')
      return firstImage ? firstImage.url : ''
    },
    previewMediaList(item) {
      return this.parseProductMedia(item && item.imageUrls, this.productCoverUrl(item))
    },
    formatPrice(value) {
      if (value === undefined || value === null || value === '') return '¥0.00'
      const amount = Number(value)
      if (Number.isNaN(amount)) return `¥${value}`
      return `¥${amount.toFixed(2)}`
    },
    openPreview(item) {
      const mediaList = this.previewMediaList(item)
      const video = mediaList.find((media) => media.type === 'video')
      const image = mediaList.find((media) => media.type === 'image')
      this.previewMedia = video || image || null
      this.previewPoster = image ? image.url : this.productCoverUrl(item)
      if (this.previewMedia) {
        this.previewVisible = true
      }
    },
    clearPreview() {
      this.previewVisible = false
      this.previewMedia = null
      this.previewPoster = ''
    },
    handleSelect(item) {
      this.$emit('select', item)
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.picker-toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.picker-toolbar .el-input {
  flex: 1;
}

.picker-body {
  min-height: 280px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.product-card {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.product-card:hover,
.product-card.active {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.12);
}

.product-card-cover-wrap {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

.product-card-cover {
  display: block;
  width: 100%;
  height: 148px;
  object-fit: cover;
  background: #f5f7fa;
}

.product-card-cover-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 13px;
}

.product-card-play {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #303133;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.product-card-play i {
  margin-left: 2px;
}

.product-card-title {
  margin-top: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  font-size: 13px;
}

.product-card-price {
  color: #f56c6c;
  font-weight: 600;
}

.product-card-category {
  flex-shrink: 0;
  color: #409eff;
}

.picker-footer {
  margin-top: 16px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.picker-footer-tip {
  color: #909399;
  font-size: 13px;
}

.preview-media {
  display: block;
  width: 100%;
  max-height: 360px;
  background: #000;
}

.preview-image {
  object-fit: contain;
  background: #f5f7fa;
}
</style>
